/**
 * 快速导航组件
 * 提供快速访问最近文档、收藏文档、常用功能等导航功能
 */

import { BaseDocument } from '@/types'
import { CloseOutlined,HistoryOutlined,RocketOutlined,SearchOutlined } from '@ant-design/icons'
import { documentManager } from '@services/document/documentManager'
import {
DOCUMENT_TYPE_ORDER,
getDocumentCreatePath,
getDocumentEditPath,
getDocumentTypeColor,
getDocumentTypeLabel,
renderDocumentTypeIcon,
} from '@utils/documentDisplay'
import { formatRelativeTime } from '@utils/index'
import {
Avatar,
Badge,
Button,
Drawer,
Empty,
Input,
List,
message,
Space,
Tabs,
Tag,
Typography,
} from 'antd'
import React,{ useCallback,useEffect,useMemo,useRef,useState } from 'react'
import { useNavigate } from 'react-router-dom'

const { Title } = Typography
const { Search } = Input

/**
 * 快速导航组件属性接口
 */
export interface QuickNavigationProps {
  /** 是否显示 */
  visible: boolean
  /** 关闭回调 */
  onClose: () => void
  /** 自定义类名 */
  className?: string
}

/**
 * 快速操作配置
 */
const QUICK_ACTIONS = DOCUMENT_TYPE_ORDER.map(type => ({
  key: `new-${type}`,
  title: `新建${getDocumentTypeLabel(type)}`,
  icon: renderDocumentTypeIcon(type),
  color: getDocumentTypeColor(type),
  path: getDocumentCreatePath(type),
}))

/**
 * 快速导航组件
 */
const QuickNavigation: React.FC<QuickNavigationProps> = ({ visible, onClose, className = '' }) => {
  const navigate = useNavigate()

  const [recentDocuments, setRecentDocuments] = useState<BaseDocument[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<BaseDocument[]>([])
  const [searching, setSearching] = useState(false)
  const [activeTab, setActiveTab] = useState('recent')
  const latestSearchRequest = useRef(0)

  /**
   * 加载最近文档
   */
  const loadRecentDocuments = useCallback(async () => {
    try {
      const stats = await documentManager.getDocumentStats()
      if (stats.recentActivity) {
        // 统计只保存活动摘要，回查完整文档可以避免使用已删除或过期的标题、标签快照。
        const recentIds = stats.recentActivity.slice(0, 10).map(item => item.documentId)
        const documents = await Promise.all(
          recentIds.map(id => documentManager.getDocumentById(id))
        )
        setRecentDocuments(documents.filter(Boolean) as BaseDocument[])
      }
    } catch {
      message.error('加载最近文档失败')
    }
  }, [])

  /**
   * 加载数据
   */
  useEffect(() => {
    if (visible) {
      loadRecentDocuments()
    }
  }, [visible, loadRecentDocuments])

  /**
   * 搜索文档
   */
  const searchDocuments = useCallback(async (query: string) => {
    const normalizedQuery = query.trim()
    const requestId = latestSearchRequest.current + 1
    latestSearchRequest.current = requestId

    if (!normalizedQuery) {
      setSearchResults([])
      setActiveTab('recent')
      return
    }

    try {
      setSearching(true)
      const results = await documentManager.searchDocuments(normalizedQuery, {
        limit: 10,
      })
      // 输入变化会触发并发请求，只接受最新一次结果，避免慢请求覆盖新关键词。
      if (latestSearchRequest.current === requestId) {
        setSearchResults(results)
        setActiveTab('search')
      }
    } catch {
      if (latestSearchRequest.current === requestId) {
        message.error('搜索文档失败')
      }
    } finally {
      if (latestSearchRequest.current === requestId) {
        setSearching(false)
      }
    }
  }, [])

  /**
   * 处理搜索
   */
  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value)
      searchDocuments(value)
    },
    [searchDocuments]
  )

  /**
   * 导航到文档
   */
  const navigateToDocument = useCallback(
    (document: BaseDocument) => {
      navigate(getDocumentEditPath(document.type, document.id))
      onClose()
    },
    [navigate, onClose]
  )

  /**
   * 快速创建文档
   */
  const handleQuickAction = useCallback(
    (action: (typeof QUICK_ACTIONS)[number]) => {
      navigate(action.path)
      onClose()
    },
    [navigate, onClose]
  )

  /**
   * 渲染文档列表项
   */
  const renderDocumentItem = useCallback(
    (document: BaseDocument) => (
      <List.Item
        key={document.id}
        onClick={() => navigateToDocument(document)}
        style={{ cursor: 'pointer' }}
        className='hover:bg-gray-50 transition-colors'
      >
        <List.Item.Meta
          avatar={
            <Avatar
              icon={renderDocumentTypeIcon(document.type)}
              style={{
                backgroundColor: getDocumentTypeColor(document.type),
                color: '#fff',
              }}
            />
          }
          title={
            <Space>
              <span>{document.title}</span>
              <Tag color={getDocumentTypeColor(document.type)}>
                {getDocumentTypeLabel(document.type)}
              </Tag>
            </Space>
          }
          description={
            <div>
              <div className='text-gray-500 text-sm'>{formatRelativeTime(document.updatedAt)}</div>
              {document.tags.length > 0 && (
                <div className='mt-1'>
                  {document.tags.slice(0, 3).map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                  {document.tags.length > 3 && <Tag>+{document.tags.length - 3}</Tag>}
                </div>
              )}
            </div>
          }
        />
      </List.Item>
    ),
    [navigateToDocument]
  )

  const tabItems = useMemo(
    () => [
      {
        key: 'recent',
        label: (
          <Space>
            <HistoryOutlined />
            最近文档
            {recentDocuments.length > 0 && <Badge count={recentDocuments.length} size='small' />}
          </Space>
        ),
        children: (
          <List
            dataSource={recentDocuments}
            renderItem={renderDocumentItem}
            locale={{ emptyText: <Empty description='暂无最近文档' /> }}
          />
        ),
      },
      ...(searchQuery
        ? [
            {
              key: 'search',
              label: (
                <Space>
                  <SearchOutlined />
                  搜索结果
                  {searchResults.length > 0 && <Badge count={searchResults.length} size='small' />}
                </Space>
              ),
              children: (
                <List
                  loading={searching}
                  dataSource={searchResults}
                  renderItem={renderDocumentItem}
                  locale={{ emptyText: <Empty description='没有找到相关文档' /> }}
                />
              ),
            },
          ]
        : []),
    ],
    [recentDocuments, renderDocumentItem, searchQuery, searchResults, searching]
  )

  return (
    <Drawer
      title={
        <Space>
          <RocketOutlined />
          快速导航
        </Space>
      }
      placement='right'
      width={400}
      open={visible}
      onClose={onClose}
      className={className}
      extra={<Button type='text' icon={<CloseOutlined />} onClick={onClose} />}
    >
      <div className='quick-navigation-content'>
        {/* 搜索框 */}
        <div className='mb-4'>
          <Search
            placeholder='快速搜索文档...'
            value={searchQuery}
            onChange={event => handleSearch(event.target.value)}
            loading={searching}
            allowClear
          />
        </div>

        {/* 快速操作 */}
        <div className='mb-6'>
          <Title level={5}>
            <Space>
              <RocketOutlined />
              快速创建
            </Space>
          </Title>
          <div className='grid grid-cols-2 gap-2'>
            {QUICK_ACTIONS.map(action => (
              <Button
                key={action.key}
                type='dashed'
                icon={action.icon}
                onClick={() => handleQuickAction(action)}
                className='h-12 text-left'
                style={{ borderColor: action.color }}
              >
                <div className='text-xs'>{action.title}</div>
              </Button>
            ))}
          </div>
        </div>

        {/* 文档列表 */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </div>
    </Drawer>
  )
}

export default QuickNavigation
